import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";

import "./Navbar.scss";
import Svg from "../svg/Svg";
import { logoEsic, HeaderProfile } from "../svg/svgImageList";
import Button from "../button/Button";
import BreadcrumbItem from "./BreadcrumbItem";
import Language from "../../services/translation/language";
import useThemeService from "../../services/theme-service/ThemeService";
import { translationService } from "../../services/translation/TranslationService";
import { currentUserService } from "../../services/user/CurrentUserStorage";
import usePageTitle from "../../services/page-title/PageTitleKey";
import HeaderProfileMenu from "./HeaderProfileMenu";
import OrganizationSelector from "./OrganizationSelector";
import useMainMenuService from "../../services/main-menu/MainMenuService";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import { Dropdown, DropdownOption } from "../dropdown/Dropdown";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";
import { Breadcrumb } from "../../services/breadcrumbs/BreadcrumbModel";
import useRouteService from "../../services/route-service/RouteService";
import { Flags } from "../../generated/FeatureFlags";

interface NavbarProps {
  hasAccessToOrganization?: boolean;
}

export default observer(function Navbar(props: NavbarProps) {
  const location = useLocation();

  const { pageTitleKey, pageTitleContext } = usePageTitle();
  const { theme, themeClass } = useThemeService();
  const { menuCategories } = useMainMenuService();
  const { hasAccessToOrganization } = { ...props };

  const [menuCategoryName, setMenuCategoryName] = useState("");
  const [menuCategoryPath, setMenuCategoryPath] = useState("");
  const [showHeaderProfileMenu, setShowHeaderProfileMenu] = useState(false);

  const myAccountButtonRef = useRef(null);

  const path = location.pathname.split("/");
  const { isUserOnProjects, isUserOnJobs } = useRouteService();

  const getProfilePicture = useCallback(() => {
    const profilePictureUrl = currentUserService.currentUser.image?.url;
    if (!profilePictureUrl) return;
    return <img className={"profile-mini-picture"} src={profilePictureUrl} alt="Profile" />;
  }, []);

  const getBreadcrumbTextKey = useCallback(() => {
    return menuCategoryName && `general.${menuCategoryName}`;
  }, [menuCategoryName]);

  const getPageNameKey = useCallback(() => {
    return getBreadcrumbTextKey() === pageTitleKey ? "" : pageTitleKey;
  }, [getBreadcrumbTextKey, pageTitleKey]);

  const getPageNameContext = useCallback(() => {
    return getBreadcrumbTextKey() === pageTitleKey ? undefined : pageTitleContext;
  }, [getBreadcrumbTextKey, pageTitleContext, pageTitleKey]);

  const languageOptions: Array<DropdownOption> = [];
  Object.keys(Language).forEach((lang) => {
    if (lang === Language.keys && !featureFlagsStorage.isFeatureEnabled(Flags.Show_Translation_Keys_Option_In_Language_Dropdown)) {
      return;
    }
    languageOptions.push({
      id: (Language as any)[lang],
      name: (Language as any)[lang].toUpperCase(),
    });
  });

  useEffect(() => {
    const mainMenuItemFromPath =
      menuCategories.find((x) => path.includes(x.name)) ||
      menuCategories.find((x) => x.aliases && x.aliases.some((a) => path.includes(a)));

    if (mainMenuItemFromPath) {
      setMenuCategoryName(mainMenuItemFromPath.name);
      setMenuCategoryPath(mainMenuItemFromPath.path || mainMenuItemFromPath.name);
    } else {
      setMenuCategoryName(path[1] || "");
      setMenuCategoryPath(path[1] || "");
    }
  }, [location.pathname, menuCategories, path]);

  return (
    <>
      <div className={themeClass(["navbar"])}>
        <div className={themeClass(["navbar-breadcrumb"])}>
          <NavLink to="/">
            <Svg svg={logoEsic[theme]} className="esic-logo" />
          </NavLink>
          {(isUserOnProjects() || isUserOnJobs()) ?
            breadcrumbsStorage.breadcrumbs.map((breadcrumb: Breadcrumb, index) => (
              <BreadcrumbItem
                key={index}
                textKey={breadcrumb.textKey}
                to={breadcrumb.to}
                textContext={breadcrumb.textContext}
                isEllipsis={breadcrumb.isEllipsis}
              />
            )) :
            <>
              <BreadcrumbItem textKey={getBreadcrumbTextKey()} to={`/${menuCategoryPath}`} />
              <BreadcrumbItem textKey={getPageNameKey()} textContext={getPageNameContext()} />
            </>
          }
        </div>

        <div className="navbar-user-actions">
          {hasAccessToOrganization && currentUserService.currentUser.currentOrganization?.id ? (
            <OrganizationSelector />
          ) : (
            <></>
          )}
          <Dropdown
            options={languageOptions}
            selectedOption={{
              id: translationService.language,
              name: translationService.language.toUpperCase(),
            }}
            onChange={(newLanguage: DropdownOption) => {
              translationService.changeLanguage(newLanguage.id.toString() as Language);
            }}
            onDarkBackground
          />
          <Button
            id="navbar-header-profile"
            type="button"
            ref={myAccountButtonRef}
            textKey={currentUserService.currentUser.name || ""}
            icon={{ svg: HeaderProfile, className: themeClass(["header-profile-icon"]) }}
            component={getProfilePicture()}
            className={themeClass(["navbar-header-profile"])}
            onClick={() => setShowHeaderProfileMenu(true)}
          />
        </div>
      </div>

      <HeaderProfileMenu
        isVisible={showHeaderProfileMenu}
        anchorElement={myAccountButtonRef.current}
        close={() => setShowHeaderProfileMenu(false)}
      />
    </>
  );
});
